import {
  type UserNotificationSeverity,
  type UserNotificationStatus,
} from '@automatick/shared-ts';
import {
  type UserNotificationDeliveryResponse,
  type UserNotificationPreferencesResponse,
  type UserNotificationResponse,
} from '@automatick/shared-ng';
import { type RealtimeConnectionHealth } from '../shared/realtime/realtime-connection-health';

export interface UserNotificationQueryOptions {
  readonly statuses?: readonly UserNotificationStatus[];
  readonly categories?: readonly string[];
  readonly severities?: readonly UserNotificationSeverity[];
  readonly actionRequiredOnly?: boolean;
  readonly includeDismissed?: boolean;
  readonly take?: number;
}

export const NotificationLifecyclePhase = {
  Idle: 'idle',
  Loading: 'loading',
  Ready: 'ready',
  Empty: 'empty',
  Error: 'error',
  Partial: 'partial',
} as const;

export type NotificationLifecyclePhase =
  (typeof NotificationLifecyclePhase)[keyof typeof NotificationLifecyclePhase];

export interface UserNotificationCenterState {
  readonly notifications: readonly UserNotificationResponse[];
  readonly deliveryHistory: readonly UserNotificationDeliveryResponse[];
  readonly selectedDeliveryNotificationId: string;
  readonly preferences: UserNotificationPreferencesResponse | null;
  readonly unreadCount: number;
  readonly actionRequiredCount: number;
  readonly isLoading: boolean;
  readonly isDeliveryHistoryLoading: boolean;
  readonly isPreferencesSaving: boolean;
  readonly notificationPhase: NotificationLifecyclePhase;
  readonly deliveryPhase: NotificationLifecyclePhase;
  readonly preferencesPhase: NotificationLifecyclePhase;
  readonly activeActionNotificationIds: readonly string[];
  readonly isRealtimeConnected: boolean;
  readonly realtimeHealth: RealtimeConnectionHealth;
  readonly notificationMessage: string;
  readonly actionMessage: string;
  readonly deliveryMessage: string;
  readonly preferencesMessage: string;
  readonly realtimeMessage: string;
  /** Compatibility projection for consumers outside the notification route. */
  readonly errorMessage: string;
}
